import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiArrowLeft, FiSettings, FiDatabase, 
  FiDownload, FiUpload, FiAlertCircle, FiLayout, FiUser, FiMonitor, FiCheck, 
  FiUsers, FiShield, FiFileText, FiUnlock, FiLock, FiRefreshCw, FiFilePlus, 
  FiActivity, FiSearch, FiKey, FiFile, FiCpu, FiGlobe, FiImage, FiMail, 
  FiTag, FiShare2, FiTwitter 
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase/supabase';
import SimulationStepEditor from './AdminPanel/SimulationStepEditor';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

const AdminPanel = ({ tickets, onUpdateTickets, onBack }) => {
  const [activeTab, setActiveTab] = useState('challenges');
  const [editingTicket, setEditingTicket] = useState(null);
  const [formData, setFormData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [actionStatus, setActionStatus] = useState({ message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [resettingUser, setResettingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [siteSettings, setSiteSettings] = useState({
    site_name: '',
    logo_url: '',
    favicon_url: '',
    support_email: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image_url: '',
    twitter_handle: ''
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'users') fetchProfiles();
    if (activeTab === 'settings') fetchSiteSettings();
  }, [activeTab]);

  const fetchSiteSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings_1777044382647')
        .select('*')
        .eq('id', 1)
        .single();
      
      if (error) throw error;
      if (data) setSiteSettings(data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setIsSavingSettings(true);
    
    // CRITICAL FIX: Destructure to remove 'id' from the update payload
    // Sending the primary key in the body of an update can cause failures in some Supabase configs
    const { id, updated_at, ...cleanSettings } = siteSettings;

    try {
      const { error } = await supabase
        .from('site_settings_1777044382647')
        .update({
          ...cleanSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) throw error;
      showStatus('Site Config Saved');
      // Re-fetch to ensure local state is synced with DB
      fetchSiteSettings();
    } catch (err) {
      console.error('Save error:', err);
      showStatus('Failed to save settings', 'error');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const showStatus = (message, type = 'success') => {
    setActionStatus({ message, type });
    setTimeout(() => setActionStatus({ message: '', type: '' }), 4000);
  };

  const fetchProfiles = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase.from('profiles_1776929082584').select('*');
      if (error) throw error;
      setProfiles(data || []);
    } catch (err) {
      showStatus('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUserVerification = async (userId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('profiles_1776929082584')
        .update({ is_verified: !currentStatus })
        .eq('id', userId);
      if (error) throw error;
      fetchProfiles();
      showStatus(currentStatus ? 'User Deactivated' : 'User Activated');
    } catch (err) {
      showStatus('Update failed', 'error');
    }
  };

  const toggleTier2Access = async (userId, currentAccess) => {
    try {
      const { error } = await supabase
        .from('profiles_1776929082584')
        .update({ tier2_access: !currentAccess })
        .eq('id', userId);
      if (error) throw error;
      fetchProfiles();
      showStatus(currentAccess ? 'Tier 2 Revoked' : 'Tier 2 Granted');
    } catch (err) {
      showStatus('Update failed', 'error');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    showStatus(`Password reset generated for ${resettingUser.email}`);
    setResettingUser(null);
    setNewPassword('');
  };

  const handleExportJSON = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(tickets, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "netdesk_simulations_backup.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
    showStatus('JSON Backup Exported');
  };

  const handleExportPDF = () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF();
      const academyName = siteSettings.site_name || 'NetDesk Pro Academy';
      doc.setFontSize(22);
      doc.setTextColor(30, 41, 59);
      doc.text(academyName, 14, 22);
      doc.setFontSize(12);
      doc.setTextColor(100, 116, 139);
      doc.text('Simulation Curriculum & Logic Guide', 14, 30);
      doc.line(14, 34, 196, 34);
      let yPos = 45;
      tickets.forEach((ticket) => {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFontSize(14);
        doc.setTextColor(37, 99, 235);
        doc.text(`${ticket.id}: ${ticket.title}`, 14, yPos);
        yPos += 7;
        doc.setFontSize(10);
        doc.setTextColor(71, 85, 105);
        doc.text(`Tier: ${ticket.tier} | Category: ${ticket.category} | Priority: ${ticket.priority}`, 14, yPos);
        yPos += 10;
        ticket.simulation.steps.forEach((step) => {
          doc.setFontSize(11);
          doc.setTextColor(15, 23, 42);
          const splitQuestion = doc.splitTextToSize(`Question: ${step.question}`, 170);
          doc.text(splitQuestion, 14, yPos);
          yPos += (splitQuestion.length * 6);
          step.options.forEach((opt) => {
            doc.setFontSize(10);
            doc.setTextColor(opt.correct ? [5, 150, 105] : [220, 38, 38]);
            const optText = `[${opt.correct ? 'CORRECT' : 'WRONG'}] ${opt.text} (${opt.points} XP)`;
            const splitOpt = doc.splitTextToSize(optText, 160);
            doc.text(splitOpt, 20, yPos);
            yPos += (splitOpt.length * 5);
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            const splitFeedback = doc.splitTextToSize(`Feedback: ${opt.feedback}`, 160);
            doc.text(splitFeedback, 25, yPos);
            yPos += (splitFeedback.length * 5) + 2;
          });
          yPos += 5;
        });
        yPos += 10;
      });
      doc.save(`${academyName.toLowerCase().replace(/\s/g, '_')}_curriculum.pdf`);
      showStatus('PDF Curriculum Generated');
    } catch (err) {
      showStatus('PDF generation failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportWord = async () => {
    setIsExporting(true);
    try {
      const academyName = siteSettings.site_name || 'NetDesk Pro Academy';
      const docChildren = [
        new Paragraph({ text: academyName, heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
        new Paragraph({ text: "Official Simulation Logic & SOP Guide", heading: HeadingLevel.HEADING_2, alignment: AlignmentType.CENTER }),
        new Paragraph({ text: "" })
      ];
      tickets.forEach(ticket => {
        docChildren.push(
          new Paragraph({
            children: [new TextRun({ text: `${ticket.id}: ${ticket.title}`, bold: true, size: 28, color: "2563EB" })],
            heading: HeadingLevel.HEADING_3
          }),
          new Paragraph({
            children: [new TextRun({ text: `Tier: ${ticket.tier} | Category: ${ticket.category} | Priority: ${ticket.priority}`, italics: true, color: "64748B" })]
          })
        );
        ticket.simulation.steps.forEach(step => {
          docChildren.push(
            new Paragraph({ children: [new TextRun({ text: `Question: `, bold: true }), new TextRun({ text: step.question })], spacing: { before: 200 } })
          );
          step.options.forEach(opt => {
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({ text: opt.correct ? "● [CORRECT] " : "○ [WRONG] ", color: opt.correct ? "059669" : "DC2626", bold: true }),
                  new TextRun({ text: `${opt.text} (${opt.points} XP)` }),
                  new TextRun({ text: `\nFeedback: ${opt.feedback}`, italics: true, color: "64748B", size: 18 })
                ],
                indent: { left: 720 }
              })
            );
          });
        });
        docChildren.push(new Paragraph({ text: "", border: { bottom: { color: "E2E8F0", space: 1, value: "single", size: 6 } } }));
      });
      const doc = new Document({ sections: [{ properties: {}, children: docChildren }] });
      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${academyName.toLowerCase().replace(/\s/g, '_')}_curriculum.docx`);
      showStatus('Word Document Generated');
    } catch (err) {
      showStatus('Word generation failed', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (Array.isArray(imported)) {
            onUpdateTickets(imported);
            showStatus('Simulations Imported');
          }
        } catch (err) {
          showStatus('Invalid backup file format', 'error');
        }
      };
      reader.readAsText(file);
    }
  };

  const startEditing = (ticket) => {
    setEditingTicket(ticket);
    setFormData(JSON.parse(JSON.stringify(ticket)));
  };

  const saveTicket = () => {
    const updated = tickets.map(t => t.id === formData.id ? formData : t);
    onUpdateTickets(updated);
    setEditingTicket(null);
    showStatus('Simulation Updated');
  };

  const filteredProfiles = profiles.filter(p => 
    p.email?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-50 text-slate-900 overflow-hidden">
      <div className="bg-slate-900 text-white p-4 md:px-8 md:py-6 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 md:gap-6 shadow-2xl shrink-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto">
          <button onClick={onBack} className="flex items-center space-x-2 px-4 py-2.5 sm:py-2 bg-slate-800 hover:bg-slate-700 rounded-xl transition-all border border-slate-700 text-xs font-black uppercase tracking-widest shrink-0 w-full sm:w-auto justify-center sm:justify-start">
            <SafeIcon icon={FiArrowLeft} />
            <span>Exit Console</span>
          </button>
          <div className="hidden sm:block h-8 w-px bg-slate-800 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex wrap items-center gap-2">
              <h1 className="text-lg md:text-xl font-black tracking-tight uppercase">Admin Console</h1>
              <span className="bg-amber-500 text-[10px] font-black px-1.5 py-0.5 rounded text-slate-900 shrink-0">ADMIN MODE</span>
            </div>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5 truncate">NetDesk Kernel 4.2.0-STABLE</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full xl:w-auto">
          <AnimatePresence>
            {actionStatus.message && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center space-x-2 shrink-0 ${actionStatus.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                <SafeIcon icon={FiCheck} />
                <span>{actionStatus.message}</span>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="grid grid-cols-2 sm:flex bg-slate-800 p-1 rounded-xl border border-slate-700 w-full sm:w-auto gap-1 sm:gap-0 font-black">
            <button onClick={() => setActiveTab('challenges')} className={`flex items-center justify-center px-4 py-2 rounded-lg text-[10px] uppercase tracking-widest transition-all ${activeTab === 'challenges' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}>Simulations</button>
            <button onClick={() => setActiveTab('users')} className={`flex items-center justify-center px-4 py-2 rounded-lg text-[10px] uppercase tracking-widest transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}>Users</button>
            <button onClick={() => setActiveTab('settings')} className={`flex items-center justify-center px-4 py-2 rounded-lg text-[10px] uppercase tracking-widest transition-all ${activeTab === 'settings' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}>Settings</button>
            <button onClick={() => setActiveTab('backup')} className={`flex items-center justify-center px-4 py-2 rounded-lg text-[10px] uppercase tracking-widest transition-all ${activeTab === 'backup' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-200'}`}>Maintenance</button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-12 bg-slate-50">
        <AnimatePresence mode="wait">
          {activeTab === 'challenges' && (
            <motion.div key="challenges" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-6">
              {editingTicket ? (
                <div className="bg-white p-6 rounded-2xl border border-slate-200">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-slate-800">Edit Simulation: {formData.id}</h3>
                    <div className="flex space-x-2">
                      <button onClick={() => setEditingTicket(null)} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-slate-200">Cancel</button>
                      <button onClick={saveTicket} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold uppercase tracking-widest flex items-center space-x-2 hover:bg-blue-700 shadow-lg"><SafeIcon icon={FiSave} /><span>Save Changes</span></button>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Incident Title</label>
                      <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full mt-1 p-3 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">Decision Steps</h4>
                      <div className="space-y-4">
                        {formData.simulation?.steps?.map((step, idx) => (
                          <SimulationStepEditor key={step.id} step={step} onUpdate={updatedStep => {
                            const newSteps = [...formData.simulation.steps];
                            newSteps[idx] = updatedStep;
                            setFormData({ ...formData, simulation: { ...formData.simulation, steps: newSteps } });
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tickets.map(t => (
                    <div key={t.id} className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-lg hover:border-blue-200 transition-all group flex flex-col h-full">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-xs font-black text-slate-400">{t.id}</span>
                        <span className={`text-[10px] uppercase font-black tracking-widest px-2 py-1 rounded-lg ${t.tier === 2 ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`}>Tier {t.tier}</span>
                      </div>
                      <h4 className="text-sm font-bold text-slate-800 mb-4 line-clamp-2 flex-1">{t.title}</h4>
                      <button onClick={() => startEditing(t)} className="w-full py-2.5 bg-slate-50 group-hover:bg-blue-50 text-slate-600 group-hover:text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center space-x-2 transition-all">
                        <SafeIcon icon={FiEdit2} />
                        <span>Edit Logic</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'users' && (
            <motion.div key="users" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h2 className="text-2xl font-black text-slate-800 tracking-tight">User Management</h2>
                  <p className="text-sm text-slate-500 font-medium">Approve analysts and manage tier access</p>
                </div>
                <div className="relative w-full sm:w-auto">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" />
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <tr>
                        <th className="p-4 pl-6">Analyst</th>
                        <th className="p-4">Role</th>
                        <th className="p-4">Academy Status</th>
                        <th className="p-4">Tier 2 Clearance</th>
                        <th className="p-4 pr-6">Admin</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {loadingUsers ? (
                        <tr><td colSpan="5" className="p-8 text-center text-slate-500 font-bold text-sm">Loading network profiles...</td></tr>
                      ) : filteredProfiles.length === 0 ? (
                        <tr><td colSpan="5" className="p-8 text-center text-slate-500 font-bold text-sm">No analysts found.</td></tr>
                      ) : filteredProfiles.map(p => (
                        <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                          <td className="p-4 pl-6">
                            <div className="font-bold text-slate-800 text-sm">{p.full_name || 'Unnamed Agent'}</div>
                            <div className="text-xs text-slate-500">{p.email}</div>
                          </td>
                          <td className="p-4">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 bg-slate-100 px-2 py-1 rounded-lg border border-slate-200">
                              {p.role || 'Junior Analyst'}
                            </span>
                          </td>
                          <td className="p-4">
                            <button onClick={() => toggleUserVerification(p.id, p.is_verified)} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg transition-all ${p.is_verified ? 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100' : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100'}`}>
                              {p.is_verified ? 'Active' : 'Awaiting Approval'}
                            </button>
                          </td>
                          <td className="p-4">
                            <button onClick={() => toggleTier2Access(p.id, p.tier2_access)} className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg transition-all flex items-center space-x-1 ${p.tier2_access ? 'bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100' : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'}`}>
                              <SafeIcon icon={p.tier2_access ? FiUnlock : FiLock} />
                              <span>{p.tier2_access ? 'Granted' : 'Locked'}</span>
                            </button>
                          </td>
                          <td className="p-4 pr-6">
                            <button onClick={() => setResettingUser(p)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100" title="Reset Password">
                              <SafeIcon icon={FiKey} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div key="settings" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto space-y-10 pb-20">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-black text-slate-800 tracking-tight">Platform Configuration</h2>
                  <p className="text-sm text-slate-500 font-medium">Manage branding, SEO, and global metadata</p>
                </div>
                <button onClick={handleUpdateSettings} disabled={isSavingSettings} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all flex items-center space-x-2">
                  {isSavingSettings ? <FiRefreshCw className="animate-spin" /> : <FiSave />}
                  <span>Save Configuration</span>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-8">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiLayout} className="text-blue-500" />
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">General Branding</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Academy Name</label>
                        <div className="relative">
                          <SafeIcon icon={FiMonitor} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="text" value={siteSettings.site_name} onChange={(e) => setSiteSettings({ ...siteSettings, site_name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Support Email</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="email" value={siteSettings.support_email} onChange={(e) => setSiteSettings({ ...siteSettings, support_email: e.target.value })} className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Logo URL</label>
                        <div className="relative">
                          <SafeIcon icon={FiImage} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="text" value={siteSettings.logo_url} onChange={(e) => setSiteSettings({ ...siteSettings, logo_url: e.target.value })} placeholder="https://..." className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Favicon URL (.ico/.png)</label>
                        <div className="relative">
                          <SafeIcon icon={FiGlobe} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                          <input type="text" value={siteSettings.favicon_url} onChange={(e) => setSiteSettings({ ...siteSettings, favicon_url: e.target.value })} placeholder="https://..." className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-6">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiTag} className="text-emerald-500" />
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Search Optimization (SEO)</h3>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Meta Title</label>
                      <input type="text" value={siteSettings.seo_title} onChange={(e) => setSiteSettings({ ...siteSettings, seo_title: e.target.value })} placeholder="Appears in browser tab" className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 px-4 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Meta Description</label>
                      <textarea value={siteSettings.seo_description} onChange={(e) => setSiteSettings({ ...siteSettings, seo_description: e.target.value })} rows={3} className="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-sm font-bold text-slate-700 outline-none resize-none" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Keywords (Comma Separated)</label>
                      <input type="text" value={siteSettings.seo_keywords} onChange={(e) => setSiteSettings({ ...siteSettings, seo_keywords: e.target.value })} placeholder="training,support,certificate..." className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 px-4 text-sm font-bold text-slate-700 outline-none" />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-slate-900 p-8 rounded-[2rem] text-white space-y-6">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiShare2} className="text-blue-400" />
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Social Media (OG)</h3>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">OG Share Image URL</label>
                      <input type="text" value={siteSettings.og_image_url} onChange={(e) => setSiteSettings({ ...siteSettings, og_image_url: e.target.value })} placeholder="1200x630 recomendado" className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 px-4 text-xs font-bold text-slate-300 outline-none" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Twitter Handle</label>
                      <div className="relative">
                        <SafeIcon icon={FiTwitter} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input type="text" value={siteSettings.twitter_handle} onChange={(e) => setSiteSettings({ ...siteSettings, twitter_handle: e.target.value })} placeholder="@username" className="w-full bg-slate-800 border border-slate-700 rounded-xl py-3 pl-11 pr-4 text-xs font-bold text-slate-300 outline-none" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'backup' && (
            <motion.div key="backup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto space-y-6">
              <div className="bg-white p-8 md:p-12 rounded-[2.5rem] border border-slate-200 shadow-sm text-center">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiDatabase} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-black text-slate-800 mb-2">System Maintenance</h3>
                <p className="text-sm text-slate-500 mb-8 max-w-md mx-auto leading-relaxed">
                  Backup, restore, and export the entire simulation database including logic, questions, and feedback.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <button onClick={handleExportJSON} className="px-6 py-4 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center space-x-2 shadow-xl hover:bg-slate-800 transition-all">
                    <SafeIcon icon={FiDownload} />
                    <span>JSON Backup</span>
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} className="px-6 py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center space-x-2 hover:bg-slate-50 hover:border-blue-200 transition-all">
                    <SafeIcon icon={FiUpload} />
                    <span>Import JSON</span>
                  </button>
                  <button onClick={handleExportPDF} disabled={isExporting} className="px-6 py-4 bg-red-50 text-red-600 border border-red-200 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center space-x-2 hover:bg-red-100 transition-all disabled:opacity-50">
                    <SafeIcon icon={FiFile} />
                    <span>Export PDF Guide</span>
                  </button>
                  <button onClick={handleExportWord} disabled={isExporting} className="px-6 py-4 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center space-x-2 hover:bg-blue-100 transition-all disabled:opacity-50">
                    <SafeIcon icon={FiFileText} />
                    <span>Export Word SOP</span>
                  </button>
                  <input type="file" ref={fileInputRef} onChange={handleImport} accept=".json" className="hidden" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {resettingUser && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-white p-8 rounded-[2rem] w-full max-w-sm border border-slate-200 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <SafeIcon icon={FiKey} className="text-xl" />
                </div>
                <h3 className="text-lg font-black text-slate-800">Reset Password</h3>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-1">For {resettingUser.email}</p>
              </div>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Temporary Password" required className="w-full p-4 border border-slate-200 rounded-xl text-sm font-bold text-center outline-none focus:ring-2 focus:ring-blue-500" />
                <div className="flex gap-3">
                  <button type="button" onClick={() => setResettingUser(null)} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-blue-700 transition-colors">Confirm</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;